import { IntelligenceFixture } from '../../types';
import { PLACEHOLDER_WHY_THIS_RESULT, PLACEHOLDER_CONFIDENCE_REASON } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

// PLA-8815: every label and figure below is a Properties-panel read of `589:28254`, not a read of
// the frame export — at the export's scale a value column is under 2px a digit, and the labels it
// gave for this card were wrong in both spelling and figures ("Incentivised activity: 74.2%" for
// what the file actually draws as "Incentivized activity: 51.2%").
export const rewardsLoyaltyCardFixtures: Record<string, IntelligenceFixture> = {
  reward_loyalty_intelligence: {
    cardKey: 'reward_loyalty_intelligence',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // FULFILMENT & REVERSAL KPIs, `589:28362` / `589:28371` / `589:28380` / `589:28389` in the order
    // the row draws them. Each `div.metric` is a label over a figure and its movement, a 227.5px
    // `div.bar` whose `i` gives the level, and a `small` caption.
    metrics: [
      /* PLA-8828: the catalogue's first four metrics on this card. None is a Figma read -- the file
         draws four KPI tiles and these are not among them; they fill the two SCORE GRIDS above that
         row, which `589:28264` drew with figures no Layer 1 metric measured. Every reading below is
         what the metric answered on UAT `generic` 2026-09-18, verbatim, and the two that answered
         nothing are `null` rather than filled in. No `delta` on any of them: no response on this
         endpoint carries a previous period. */
      {
        // `insufficient_data` / `below_da4_governed_minimum_sample_or_evidence`.
        key: 'reward_sensitivity_score',
        label: 'Reward Sensitivity Score',
        value: null,
        format: 'ratio',
        denominator: 'Normalized lift between incentivized and non-incentivized activity'
      },
      {
        // `calculated`, and a measured 0.0 rather than an absence.
        key: 'incentive_dependency_score',
        label: 'Incentive Dependency Score',
        value: 0,
        format: 'points',
        denominator: 'Mean customer IDS over scored eligible customers'
      },
      {
        // `calculated`; its own row also states 27,948 redeemed over 43,200 issued.
        key: 'reward_redemption_rate',
        label: 'Reward Redemption Rate',
        value: 64.694444,
        format: 'percent',
        denominator: 'Redeemed entitlements 27,948 · issued 43,200'
      },
      {
        // `calculated`; its own row also states 1,379,600 points earned over 278,050 burned.
        key: 'points_earn_burn_ratio',
        label: 'Points Earn-Burn Ratio',
        value: 4.961697,
        format: 'ratio',
        denominator: 'Points earned 1,379,600 · points burned 278,050'
      },
      {
        key: 'reward_fulfilment_delay_rate',
        label: 'Reward Fulfilment Delay Rate',
        value: 3.6,
        format: 'percent',
        delta: { value: 8.1, unit: 'pp', direction: 'down', sentiment: 'positive' },
        progress: 81.9 / 227.5,
        progressTone: 'warning',
        formula: 'Delayed fulfilment count 432 · attempts counts 120000'
      },
      {
        key: 'total_reversal_event_count',
        label: 'Total Reversal Event Count',
        value: 1284,
        format: 'count',
        delta: { value: 500, unit: 'count', direction: 'down', sentiment: 'positive' },
        progress: 163.79 / 227.5,
        progressTone: 'info',
        formula: 'Successful 1,136 · pending 86 · failed 42 · cancelled 20.'
      },
      {
        key: 'reversal_rate',
        label: 'Reversal Rate',
        value: 1.9,
        format: 'percent',
        delta: { value: 0.2, unit: 'pp', direction: 'up', sentiment: 'negative' },
        progress: 43.22 / 227.5,
        progressTone: 'positive',
        formula: 'Successful reversals ÷ issued or earned entitlements.'
      },
      {
        key: 'reversed_outcome',
        label: 'Reversed outcome',
        value: 2400000,
        format: 'count',
        delta: { value: 3000000, unit: 'count', direction: 'down', sentiment: 'positive' },
        progress: 100.1 / 227.5,
        progressTone: 'accent',
        formula: 'Also 310 vouchers, 86 tries, 42 stamps and 18 raffle tickets.'
      },
      {
        key: 'reversal_failure_rate',
        label: 'Reversal Failure / Retry Rate',
        // O-REV-008 on UAT `generic`, 2026-09-08, verbatim: `metric_value` 24.922971 over its own
        // three processing counts. NOT a Figma read — the file draws no tile for this metric at all
        // and `RewardsLoyaltySection` draws none either; the entry exists because the reconciliation
        // ring divides these three counts, and the design's `Reconciled 12,054 / Pending 290 /
        // Failed 97 / Mismatch 9` over a 12,450 hole was a four-way split of a population no
        // question measured. `factorsOf` ranks by magnitude, so the ring reads 2921 / 728 / 364.
        // PLA-8815.
        value: 24.92,
        format: 'percent',
        factors: [
          {
            key: 'reversal_accepted_count',
            label: 'Accepted',
            value: 2921,
            tone: 'positive'
          },
          {
            key: 'reversal_failed_retrying_count',
            label: 'Failed retrying',
            value: 728,
            tone: 'blocker'
          },
          { key: 'reversal_retrying_count', label: 'Retrying', value: 364, tone: 'blocker' }
        ]
      },
      /* PLA-8828: the catalogue's O-REV-006, bound now that every catalogue metric with a card gets
         a binding. `null` and not a figure: the endpoint answers 404 `40415` for it on UAT
         (2026-09-18), so no reading has ever been observed and no `cols` naming its unit either. A
         number here would be a review-only stand-in the live tile can never show. */
      {
        key: 'reversal_processing_sla',
        label: 'Reversal Processing SLA',
        value: null,
        format: 'count'
      }
    ],
    confidence: {
      score: 82,
      band: 'high',
      // `589:28673` is still Lorem Ipsum in the file, so the badge's reason ships the placeholder
      // rather than prose written to fill it.
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    // `589:28642` and `589:28646`, the bodies of the two boxes at y=5117 and y=5205.2. Both Lorem.
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT,
    evidenceNote: PLACEHOLDER_WHY_THIS_RESULT
  }
};

/* What the card's Figma payload is down to. Every FIGURE it carried is gone — the four score dials
   of `589:28263` / `589:28310`, the four `718:2007` tier segments, the twelve reversal bar fills of
   `589:28399` and the four `589:28651` reconciliation arcs with their 12,450 hole. Nothing on the
   wire measures reward sensitivity, points flow, tier membership or a reversal reason, and the one
   figure that WAS real — 12,450 against O-REV-001's 2,921 — contradicted the tile printed beside
   it. `RewardsLoyaltySection` keeps every one of those blocks mounted on empty rows and reads the
   ring's hole off its own `Total Reversal Event Count` tile. PLA-8815. */
export const rewardsLoyaltyVisuals = {
  // The label under the hole of the 160px ring at `589:28651`, read off `589:28670`. A label, not a
  // measurement — it names whatever total the card's own tile supplies.
  reconciliation: {
    centerLabel: 'Total events'
  }
};
